import React, { useState, useRef } from 'react';
import { Box } from '@mui/material';
import CustomAvatar from '../lib/ui-components/Avatar/CustomAvatar';
import CustomTypography from '../lib/ui-components/Typography/CustomTypography';
import CustomIconButton from '../lib/ui-components/IconButton/CustomIconButton';
import './Chat.css';
import CustomTextField from '../lib/ui-components/TextField/CustomTextField';
import SendIcon from '../../assets/chatIcons/sendIcon.svg';
import SendIconEnabled from '../../assets/chatIcons/active-sendIcon.svg';
import CloseIcon from '../../assets/images/Close.svg';
import type { ChatMessage, ChatProps } from './ChatService';

const Chat: React.FC<ChatProps> = ({
  title = 'Chat',
  subtitle,
  messages,
  currentUserId,
  onSendMessage,
  onClose,
  placeholder = 'Type your message',
  className,
  isViewOnly = false,
  maxHeight = '100vh',
  getAvatarIcon,
}) => {
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const handleSendMessage = () => {
    if (inputMessage.trim()) {
      onSendMessage(inputMessage.trim());
      setInputMessage('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const groupMessagesByDate = (messages: ChatMessage[]) => {
    const groups: { [key: string]: ChatMessage[] } = {};

    messages.forEach((message) => {
      const date = new Date(message.timestamp);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      let dateLabel: string;
      if (date.toDateString() === today.toDateString()) {
        dateLabel = 'Today';
      } else if (date.toDateString() === yesterday.toDateString()) {
        dateLabel = 'Yesterday';
      } else {
        dateLabel = date.toLocaleDateString('en-US', {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        });
      }

      if (!groups[dateLabel]) {
        groups[dateLabel] = [];
      }
      groups[dateLabel].push(message);
    });

    return groups;
  };

  const messageGroups = groupMessagesByDate(messages);

  return (
    <Box
      className={`chat-container ${className || ''}`}
      sx={{
        maxHeight: maxHeight,
      }}
    >
      {/* Header */}
      <Box className="chat-header">
        <Box className="flex align-items-center justify-space-between">
          <Box>
            <CustomTypography
              id="chat-title"
              variant="h5"
              color="textPrimary"
              className="chat-title-text"
            >
              {title}
            </CustomTypography>
            {subtitle && (
              <CustomTypography
                id="chat-subtitle"
                variant="body3"
                color="textDisabled"
              >
                {subtitle}
              </CustomTypography>
            )}
          </Box>
          {onClose && (
            <CustomIconButton
              id="chat-close-button"
              icon={<CloseIcon />}
              onClick={onClose}
              size="small"
              className="chat-close-button"
            />
          )}
        </Box>
      </Box>

      {/* Messages Container */}
      <Box className="chat-messages-container">
        {messages.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <CustomTypography
              id="chat-no-messages"
              variant="body2"
              color="textDisabled"
            >
              No messages yet. Start the conversation!
            </CustomTypography>
          </Box>
        ) : (
          Object.entries(messageGroups).map(([dateLabel, msgs]) => (
            <Box key={dateLabel}>
              {/* Date Divider */}
              <Box className="chat-date-divider-container">
                <Box className="chat-date-divider">
                  <CustomTypography
                    id="chat-date-label"
                    variant="body3"
                    className="chat-date-label"
                  >
                    {dateLabel}
                  </CustomTypography>
                </Box>
              </Box>

              {/* Messages for this date */}
              {msgs.map((msg) => {
                const isCurrentUser =
                  msg.senderId === currentUserId || msg.isCurrentUser;

                return (
                  <Box
                    key={msg.id}
                    className={`chat-message-row ${
                      isCurrentUser ? 'chat-message-row-current-user' : ''
                    }`}
                  >
                    {/* Avatar */}

                    {getAvatarIcon && msg.portalType ? (
                      getAvatarIcon(msg.portalType)
                    ) : (
                      <CustomAvatar
                        id={`chat-avatar-${msg.id}`}
                        width={36}
                        height={36}
                      >
                        {msg.senderInitials}
                      </CustomAvatar>
                    )}

                    {/* Message Content */}
                    <Box className="chat-message-content">
                      {/* Message Bubble */}
                      <Box
                        className={`chat-message-bubble ${
                          isCurrentUser
                            ? 'chat-message-bubble-current-user'
                            : ''
                        }`}
                      >
                        <Box>
                          {/* Sender Name */}
                          {isCurrentUser ? (
                            <CustomTypography
                              id={`chat-sender-${msg.id}`}
                              variant="caption"
                              color="textDisabled"
                            >
                              You
                            </CustomTypography>
                          ) : (
                            <CustomTypography
                              id={`chat-sender-${msg.id}`}
                              variant="caption"
                              color="textDisabled"
                            >
                              {msg.senderName}
                            </CustomTypography>
                          )}
                        </Box>
                        <CustomTypography
                          id={`chat-message-${msg.id}`}
                          variant="body3"
                          color="textPrimary"
                          className="chat-message-text"
                        >
                          {msg.message}
                        </CustomTypography>
                      </Box>

                      {/* Timestamp */}
                      <CustomTypography
                        id={`chat-timestamp-${msg.id}`}
                        variant="caption"
                        className={`${
                          isCurrentUser
                            ? 'chat-timestamp-current-user'
                            : 'chat-timestamp-other-user'
                        }`}
                        color="textDisabled"
                      >
                        {formatTime(new Date(msg.timestamp))}
                      </CustomTypography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))
        )}
        <Box ref={messagesEndRef} />
      </Box>
      {!isViewOnly && (
        <Box className="chat-input-area flex">
          <CustomTextField
            id="chat-input-field"
            fullWidth
            variant="outlined"
            placeholder={placeholder}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            className="chat-input-field"
          />
          <Box className="chat-send-container ml-6">
            <CustomIconButton
              id="chat-send-button"
              icon={inputMessage.trim() ? <SendIconEnabled /> : <SendIcon />}
              onClick={handleSendMessage}
              disabled={!inputMessage.trim()}
              size="small"
              className={`chat-send-button ${
                inputMessage.trim() ? 'chat-send-button-enabled' : ''
              }`}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Chat;
